/**
 * ARPIVIA — C1-B : sélecteur d'offre et composition des paires.
 *
 * CE QUE CE MODULE FAIT, ET CE QU'IL NE FAIT PAS
 * Il compose une liste de vraies variantes Shopify avec leurs quantités
 * réelles, puis délègue le prix à Shopify : ajout groupé par /cart/add.js,
 * application du code de remise natif par /cart/update.js, arbitrage du
 * palier réellement atteint par assets/arpivia-c1b-cart-sync.js, relecture du
 * panier, et affichage du total réellement retourné. Aucun montant affiché
 * après l'ajout n'est reconstruit ici.
 *
 * Les montants annoncés AVANT l'ajout viennent de Liquid, déjà formatés par
 * le filtre `money` dans la devise de présentation du visiteur. Ce module ne
 * formate aucune devise et n'écrit aucun symbole monétaire.
 *
 * OUVERTURE DU TIROIR
 * Le thème n'ajoute `auto-open` à <cart-drawer-component> que si le réglage
 * « ouvrir le panier après un ajout » est actif ; il ne l'est pas ici. Un
 * ajout réussi ne suffisait donc pas à ouvrir le tiroir. L'ouverture est
 * demandée explicitement après confirmation, sans modifier le réglage global
 * qui concerne toutes les autres pages.
 *
 * LE CTA MÈNE AU PREMIER CHOIX MANQUANT
 * Un bouton fixe qui pose le focus sur un champ situé hors de l'écran ne
 * conduit nulle part : le clavier avance, la page ne bouge pas, et le
 * visiteur ne voit rien se passer. La composition incomplète déclenche donc
 * un défilement réel jusqu'au premier groupe de choix manquant, sous
 * l'en-tête collant, et le focus n'est posé qu'une fois ce défilement
 * terminé.
 *
 * 15 SEPTEMBRE 2026
 * - Le bouton nomme le vrai choix manquant : couleur quand seule la couleur
 *   manque, et pour un lot la paire incomplète (« Paire 2 : choisir la
 *   couleur »). Un libellé d'ajout contenant [total] reçoit le montant.
 * - Un succès n'est annoncé que si toutes les paires sont réellement dans le
 *   panier ; si le code de la formule n'est pas retenu par Shopify, le
 *   visiteur en est averti au lieu de lire un succès au tarif du lot.
 * - Changement de coupe : la formule et les couleurs encore valables sont
 *   reprises sur la fiche de l'autre coupe ; une pointure d'une autre grille
 *   est redemandée explicitement.
 * - Le choix d'une couleur amène la photo de cette variante si elle existe.
 * - Ouverture du guide et erreurs de choix publiées comme repères de
 *   diagnostic, avec la famille de page réglée dans le bloc.
 *
 * 18 OCTOBRE 2026 — RÉGLAGE « UNE PAIRE OUVERTE À LA FOIS » (data.packAccordion)
 * - Chaque paire d'un lot a sa ligne repliable ; une seule est ouverte. Une
 *   paire terminée se replie sur son résumé et la première paire incomplète
 *   s'ouvre.
 * - Changer de formule reprend les choix déjà faits, paire par paire.
 * - Formules repliées sans formule cochée : le bouton et la barre fixe
 *   ramènent aux formules au lieu d'ouvrir une formule à la place du visiteur.
 * - Barre fixe : libellé d'ajout court et nombre de paires sous le produit.
 * Réglage décoché : comportement strictement inchangé.
 */

import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const ROOT = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const url = (path) => `${ROOT()}${path}`.replace(/\/{2,}/g, '/');

/** Clé d'une combinaison pointure/couleur. */
const key = (color, size) => `${color ?? ''} ${size ?? ''}`;

/** Hauteur réservée sous l'en-tête collant avant la cible d'un défilement. */
const CLEARANCE = 100;

/** Choix transmis d'une coupe à l'autre, le temps d'un changement de page. */
const CUT_KEY = 'arpivia-c1b-cut-state';
const CUT_TTL = 10 * 60 * 1000;

/**
 * Contexte de campagne des événements du parcours.
 *
 * L'angle vient de `data-c1b-angle`, écrit par assets/arpivia-c1b-angle.js
 * uniquement pour une clé présente dans la liste blanche rendue par le
 * serveur : aucune chaîne libre de l'URL n'entre ici. Les autres valeurs sont
 * lues dans l'URL puis filtrées par un jeu de caractères strict, et une clé
 * absente reste absente — rien n'est inventé, aucune donnée personnelle
 * n'est collectée.
 */
const SAFE_VALUE = /^[A-Za-z0-9_.\-]{1,64}$/;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

function campaignContext() {
  const context = {};
  const angle = document.documentElement.dataset.c1bAngle;
  if (angle) {
    context.angle_id = angle;
    context.hero_variant_id = angle;
  }
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return context;
  }
  const take = (name) => {
    const value = params.get(name);
    return value && SAFE_VALUE.test(value) ? value : null;
  };
  const creative = take('creative');
  if (creative) context.creative_id = creative;
  for (const name of UTM_KEYS) {
    const value = take(name);
    if (value) context[name] = value;
  }
  return context;
}

const reducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Élément personnalisé. Le cycle connected/disconnected garantit qu'un
 * rechargement de section dans l'éditeur ne laisse pas d'écouteur en double.
 */
class ArpiviaC1bOffer extends HTMLElement {
  #abort = null;
  /** Table des quantités saisies à la main, construite une seule fois. */
  #stockTable = null;
  /** Échange en cours dans la galerie : { index, firstId } ou null. */
  #mediaSwap = null;
  #busy = false;
  /** Code du palier en attente d'arbitrage face à un code déjà saisi. */
  #pendingCode = null;
  /** Codes indépendants déjà présents dans le panier du visiteur. */
  #pendingExisting = [];
  /** Lignes réellement ajoutées, conservées pour l'arbitrage d'un conflit. */
  #pendingLines = [];
  /** Un seul événement de succès par ajout, quel que soit le chemin suivi. */
  #announced = false;

  connectedCallback() {
    if (this.#abort) return;
    this.#abort = new AbortController();

    try {
      this.data = JSON.parse(this.querySelector('[data-c1b-data]')?.textContent || '{}');
    } catch {
      return; // Sans données exploitables, le formulaire natif reste la voie d'achat.
    }
    if (!this.data.tiers || !this.data.tiers.length) return;

    this.variants = new Map();
    for (const v of this.data.variants) this.variants.set(key(v.color, v.size), v);

    const first = this.data.tiers.find((t) => t.id === this.data.defaultTierId) || this.data.tiers[0];

    /* UNE COMPOSITION PAR FORMULE, PAS UNE SEULE QU'ON REDIMENSIONNE
       Chaque formule garde ses propres paires. Changer de formule ne perd
       donc rien, et n'a rien a memoriser ailleurs : les choix de la formule
       quittee sont toujours la si on y revient. Seules les paires de la
       formule active sont ajoutees au panier. */
    const pairsByTier = {};
    for (const t of this.data.tiers) {
      const size = Math.max(1, Number(t.quantity) || 1);
      pairsByTier[t.id] = Array.from({ length: size }, () => ({ size: null, color: null }));
    }
    // Configurateur replié à l'arrivée (réglage du bloc) : les trois formules
    // restent comparables d'un coup d'œil ; la composition s'ouvre au premier
    // clic sur une formule ou sur le bouton d'achat.
    this.state = { tierId: first.id, pairsByTier, started: false, open: !this.data.collapsedStart };
    const cutNotice = this.#restoreCutState();
    if (cutNotice || this.state.started) this.state.open = true;

    this.#bind();
    this.setAttribute('data-ready', 'true');
    // Signale au CSS que le configurateur est réellement opérationnel : c'est
    // seulement à ce moment que le sélecteur natif et les boutons natifs sont
    // retirés de l'écran. Sans JavaScript, ils restent la voie d'achat.
    document.documentElement.dataset.c1bOffer = 'on';
    this.#render();
    this.#syncColorMedia(false);
    if (cutNotice) {
      this.#setRowError(cutNotice.index, this.data.text.cutSizeReset || '');
      this.#status(this.data.text.cutSizeReset || '', 'neutral');
    }
  }

  /**
   * Reprend les choix faits sur l'autre coupe.
   *
   * Seules les valeurs qui existent réellement sur ce produit sont reprises.
   * Une pointure qui n'appartient pas à cette grille n'est jamais convertie :
   * elle est effacée et redemandée sur la première ligne concernée.
   * @returns {{index:number}|null}
   */
  #restoreCutState() {
    let saved = null;
    try {
      saved = JSON.parse(window.sessionStorage.getItem(CUT_KEY) || 'null');
      window.sessionStorage.removeItem(CUT_KEY);
    } catch {
      return null;
    }
    if (!saved || Date.now() - Number(saved.at || 0) > CUT_TTL) return null;
    if (Number(saved.productId) === Number(this.data.productId)) return null;

    const sizes = new Set([...this.variants.values()].map((v) => v.size));
    const colors = new Set([...this.variants.values()].map((v) => v.color));
    let reset = null;

    if (saved.tierId && this.state.pairsByTier[saved.tierId]) this.state.tierId = saved.tierId;
    for (const [tierId, pairs] of Object.entries(saved.pairsByTier || {})) {
      const target = this.state.pairsByTier[tierId];
      if (!target || !Array.isArray(pairs)) continue;
      pairs.forEach((p, i) => {
        if (!target[i] || !p) return;
        if (p.color && colors.has(p.color)) target[i].color = p.color;
        if (p.size && sizes.has(p.size)) target[i].size = p.size;
        else if (p.size && tierId === this.state.tierId && !reset) reset = { index: i };
      });
    }
    this.state.started = true;
    this.#track('c1b_cut_restore', { size_reset: Boolean(reset) });
    return reset;
  }

  #saveCutState() {
    try {
      window.sessionStorage.setItem(
        CUT_KEY,
        JSON.stringify({
          at: Date.now(),
          productId: this.data.productId,
          tierId: this.state.tierId,
          pairsByTier: this.state.pairsByTier,
        })
      );
    } catch {
      /* stockage indisponible : la coupe change, les choix sont à refaire */
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    if (!document.querySelector('arpivia-c1b-offer[data-ready="true"]')) {
      delete document.documentElement.dataset.c1bOffer;
    }
  }

  get #signal() {
    return this.#abort?.signal;
  }

  get tier() {
    return this.data.tiers.find((t) => t.id === this.state.tierId) || this.data.tiers[0];
  }

  // ---------------------------------------------------------------- liaisons

  #bind() {
    const on = (ev, fn) => this.addEventListener(ev, fn, { signal: this.#signal });

    on('change', (e) => {
      const t = e.target;
      const isControl = t instanceof HTMLInputElement || t instanceof HTMLSelectElement;
      if (!isControl) return;
      if (t.name === 'c1b-tier') {
        this.#selectTier(t.value);
        return;
      }
      const kind = t.dataset.c1bOption;
      if (!kind) return;
      // Le menu vide renvoie une chaine vide : c'est une absence de choix.
      this.#setOption(kind, t.value || null, Number(t.dataset.c1bIndex || 0));
    });

    on('click', (e) => {
      const cut = e.target instanceof Element ? e.target.closest('[data-c1b-cut-link]') : null;
      if (cut) {
        if (cut.getAttribute('aria-current') === 'page') {
          e.preventDefault();
          return;
        }
        this.#saveCutState();
        this.#track('c1b_cut_change', { to: cut.dataset.c1bCutLink });
        return; // navigation normale vers la fiche de l'autre coupe
      }
      const rowToggle = e.target instanceof Element ? e.target.closest('[data-c1b-row-toggle]') : null;
      if (rowToggle && this.data.packAccordion) {
        e.preventDefault();
        const i = Number(rowToggle.dataset.c1bRowToggle);
        this.state.openRow = this.state.openRow === i ? -1 : i;
        this.#render();
        return;
      }
      const head = e.target instanceof Element ? e.target.closest('.c1b-tier__head') : null;
      if (head && !this.state.open) {
        this.state.open = true;
        // Après l'éventuel changement de formule déclenché par ce même clic.
        requestAnimationFrame(() => this.#render());
      }
      const guide = e.target instanceof Element ? e.target.closest('[data-arpivia-guide-trigger]') : null;
      if (guide) this.#track('c1b_size_guide_open', { offer_tier_id: this.state.tierId });
      const el = e.target instanceof Element ? e.target.closest('[data-c1b-action]') : null;
      if (!el) return;
      e.preventDefault();
      const { c1bAction: action } = el.dataset;
      if (action === 'apply-rest') this.#applyToRemaining();
      else if (action === 'submit') this.#submit();
      else if (action === 'keep-code') this.#resolveConflict('keep');
      else if (action === 'apply-offer') this.#resolveConflict('offer');
    });

    // Le CTA fixe partage exactement cet état : il délègue ici, il n'a pas
    // de logique d'achat propre.
    this.#setupSticky();

    document.addEventListener('arpivia:c1b:request-submit', () => this.#submit(), {
      signal: this.#signal,
    });
  }

  /**
   * Affiche le CTA fixe uniquement lorsque le bouton principal a quitte
   * l'ecran, et jamais par-dessus le dernier controle du configurateur :
   * l'element observe est la fin du bloc d'action, pas le bloc entier.
   */
  /**
   * BARRE COMPACTE — VISIBILITÉ AU PIXEL PRÈS
   *
   * La barre est affichée sur toute la page, sauf lorsqu'au moins un pixel
   * du bouton d'achat du bloc d'achat, ou du CTA de bas de page
   * (`[data-c1b-final-cta]`), est visible dans la zone utile de l'écran :
   * sous l'en-tête collé, au-dessus de la barre. La zone ne dépend pas de
   * l'état de la barre (sa hauteur est mémorisée) : aucun clignotement à la
   * frontière. Même règle sur mobile et sur ordinateur.
   *
   * Recalcul à chaque défilement, redimensionnement, changement de la
   * fenêtre visuelle (barre d'adresse mobile) et changement de hauteur du
   * document (formule changée, accordéon ouvert) : les positions sont lues,
   * jamais estimées.
   */
  #setupStickyPrecise(bar, main) {
    const root = document.documentElement;
    root.dataset.c1bSticky = 'on';
    const header = document.querySelector('header-component, #header-component, .header-section, header');
    let barHeight = 0;

    const measureBar = () => {
      const wasHidden = bar.hidden;
      if (wasHidden) bar.hidden = false;
      const h = bar.getBoundingClientRect().height;
      if (wasHidden) bar.hidden = true;
      if (h > 0) {
        barHeight = h;
        root.style.setProperty('--c1b-sticky-h', `${Math.ceil(h)}px`);
      }
    };

    const headerBottom = () => {
      if (!header) return 0;
      const position = window.getComputedStyle(header).position;
      if (position !== 'sticky' && position !== 'fixed') return 0;
      return Math.max(0, header.getBoundingClientRect().bottom);
    };

    const inZone = (el) => {
      if (!el || !el.isConnected) return false;
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return false;
      const top = headerBottom();
      const bottom = window.innerHeight - barHeight;
      return r.bottom > top && r.top < bottom;
    };

    let final = document.querySelector('[data-c1b-final-cta]');
    const update = () => {
      if (!final || !final.isConnected) final = document.querySelector('[data-c1b-final-cta]');
      const hide = inZone(main) || inZone(final);
      if (bar.hidden !== hide) bar.hidden = hide;
      root.dataset.c1bStickyVisible = hide ? 'off' : 'on';
    };

    const onResize = () => {
      measureBar();
      update();
    };

    measureBar();
    update();

    const opts = { passive: true, signal: this.#signal };
    window.addEventListener('scroll', update, opts);
    window.addEventListener('resize', onResize, opts);
    window.addEventListener('load', onResize, opts);
    window.visualViewport?.addEventListener('resize', onResize, opts);
    window.visualViewport?.addEventListener('scroll', update, opts);
    let ro = null;
    if (typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(update);
      ro.observe(document.body);
      ro.observe(bar);
    }

    this.#signal?.addEventListener('abort', () => {
      ro?.disconnect();
      delete root.dataset.c1bSticky;
      delete root.dataset.c1bStickyVisible;
      root.style.removeProperty('--c1b-sticky-h');
    });
  }

  #setupSticky() {
    const bar = this.querySelector('[data-c1b-sticky]');
    const main = this.#cta();
    if (bar && main && this.dataset.stickyLayout === 'inline') {
      this.#setupStickyPrecise(bar, main);
      return;
    }
    if (!bar || !main || typeof IntersectionObserver !== 'function') return;

    // Le bouton fixe natif du theme est retire : une seule logique d'achat.
    document.documentElement.dataset.c1bSticky = 'on';

    /* La barre a deux raisons de s'effacer, et elles se cumulent : le bouton
       principal est a l'ecran, OU le dernier appel a l'action l'est
       entierement. Dans les deux cas un vrai bouton d'achat est deja visible,
       et la barre ne ferait que le doubler. */
    let mainVisible = false;
    let finalVisible = false;
    /* Barre compacte : elle s'efface dès que le bouton principal est lisible
       au-dessus d'elle, et sur deux colonnes (990 px et plus) elle n'apparaît
       qu'une fois ce bouton dépassé — la colonne d'achat reste alors visible
       sans rien masquer de ses choix. */
    const inline = this.dataset.stickyLayout === 'inline';
    const wide = inline && typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 990px)') : null;
    let mainAbove = false;
    const apply = () => {
      const hide = mainVisible || finalVisible || Boolean(wide && wide.matches && !mainAbove);
      bar.hidden = hide;
      document.documentElement.dataset.c1bStickyVisible = hide ? 'off' : 'on';
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        mainVisible = entry.isIntersecting;
        mainAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        apply();
      },
      { rootMargin: inline ? '0px 0px -88px 0px' : '0px 0px -40% 0px' }
    );
    io.observe(main);
    wide?.addEventListener?.('change', apply, { signal: this.#signal });

    /* « Entierement visible SOUS l'en-tete » : la fenetre d'observation est
       reduite de la hauteur de l'en-tete collant, et le seuil est 1 — le
       bouton doit tenir en entier dans ce qui reste. La hauteur est relue au
       redimensionnement, l'en-tete ne mesurant pas la meme chose partout. */
    const final = document.querySelector('[data-c1b-final-cta]');
    let finalObserver = null;
    const watchFinal = () => {
      if (finalObserver) finalObserver.disconnect();
      finalVisible = false;
      if (!final) return;
      const header = document.querySelector('#header-component, .header-section, header');
      let inset = 0;
      if (header) {
        try {
          const position = window.getComputedStyle(header).position;
          if (position === 'sticky' || position === 'fixed') {
            inset = Math.round(header.getBoundingClientRect().height);
          }
        } catch {
          inset = 0;
        }
      }
      finalObserver = new IntersectionObserver(
        ([entry]) => {
          finalVisible = entry.isIntersecting;
          apply();
        },
        { threshold: 1, rootMargin: `-${inset}px 0px 0px 0px` }
      );
      finalObserver.observe(final);
    };
    watchFinal();

    let resizeTimer = null;
    const onResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(watchFinal, 200);
    };
    window.addEventListener('resize', onResize, { signal: this.#signal });

    this.#signal?.addEventListener('abort', () => {
      io.disconnect();
      finalObserver?.disconnect();
      if (resizeTimer) clearTimeout(resizeTimer);
      delete document.documentElement.dataset.c1bSticky;
      delete document.documentElement.dataset.c1bStickyVisible;
    });
  }

  // ------------------------------------------------------------------ paliers

  /** Les paires de la formule affichee. */
  #pairs() {
    return this.state.pairsByTier[this.state.tierId] || [];
  }

  #selectTier(id) {
    if (id === this.state.tierId) return;
    if (!this.state.pairsByTier[id]) return;
    this.#clearAddedStatus();
    const previous = this.state.pairsByTier[this.state.tierId] || [];
    this.state.tierId = id;
    this.state.open = true;
    if (this.data.packAccordion) {
      // Les choix déjà faits suivent le visiteur d'une formule à l'autre :
      // seules les paires encore vierges de la nouvelle formule sont remplies.
      this.state.pairsByTier[id].forEach((pair, i) => {
        const from = previous[i];
        if (!from || pair.size || pair.color) return;
        if (from.size) pair.size = from.size;
        if (from.color) pair.color = from.color;
      });
      const missing = this.#firstMissing();
      this.state.openRow = missing ? missing.index : -1;
    }
    this.#track('c1b_tier_select', { offer_tier_id: id, quantity: this.tier.quantity });
    this.#render();
    this.#syncColorMedia(true);
  }

  #complete() {
    const pairs = this.#pairs();
    return pairs.length > 0 && pairs.every((p) => p.size && p.color);
  }

  /** Premier champ reellement manquant, ligne comprise. */
  #firstMissing() {
    const pairs = this.#pairs();
    for (let i = 0; i < pairs.length; i++) {
      if (!pairs[i].size) return { index: i, kind: 'size' };
      if (!pairs[i].color) return { index: i, kind: 'color' };
    }
    return null;
  }

  // ------------------------------------------------------------- composition

  #setOption(kind, value, index = 0) {
    const pair = this.#pairs()[index];
    if (!pair) return;
    if (pair[kind] !== value) this.#clearAddedStatus();
    const wasComplete = Boolean(pair.size && pair.color);
    pair[kind] = value;
    this.state.lastIndex = index;
    if (this.data.packAccordion && !wasComplete && pair.size && pair.color) {
      const missing = this.#firstMissing();
      this.state.openRow = missing ? missing.index : -1;
    }
    if (!this.state.started && value) {
      this.state.started = true;
      this.#track('c1b_compose_start', { offer_tier_id: this.state.tierId });
    }
    this.#setRowError(index, '');
    this.#render();
    if (kind === 'color') this.#syncColorMedia(true);
  }

  /**
   * PHOTO DU COLORIS EN PREMIÈRE POSITION
   *
   * La couleur retenue est celle de la dernière paire modifiée, sinon la
   * première paire qui en a une. Sa photo de variante prend la place de la
   * première photo, et la première photo prend la sienne : aucune photo n'est
   * dupliquée ni retirée. L'échange vaut pour la galerie, ses miniatures et la
   * visionneuse agrandie, pour que chaque miniature et chaque zoom montrent
   * la bonne image. Sans couleur choisie, l'ordre par défaut est rétabli.
   * Sans photo associée à la couleur, la galerie ne change pas.
   * @param {boolean} interactive - true : ramène la galerie sur la 1re photo.
   */
  #syncColorMedia(interactive) {
    const pairs = this.#pairs();
    const last = pairs[this.state.lastIndex];
    const pick = last && last.color ? last : pairs.find((p) => p.color);
    let mediaId = null;
    if (pick) {
      const all = [...this.variants.values()].filter((v) => v.color === pick.color && v.media);
      const variant = all.find((v) => v.size === pick.size) || all[0];
      if (variant) mediaId = String(variant.media);
    }

    const gallery = this.#galleryParts();
    if (!gallery) return;
    const { slideshow, lists } = gallery;
    const slides = lists[0];
    const idAt = (i) => slides[i]?.querySelector('[data-media-id]')?.getAttribute('data-media-id') || null;

    // Rétablit l'ordre par défaut, si l'échange précédent est toujours en place.
    if (this.#mediaSwap) {
      const { index, firstId } = this.#mediaSwap;
      if (idAt(index) === firstId) this.#swapMedia(lists, 0, index);
      this.#mediaSwap = null;
    }
    if (!mediaId) return;

    const index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${mediaId}"]`));
    if (index < 0) return;
    if (index > 0) {
      const firstId = idAt(0);
      this.#swapMedia(lists, 0, index);
      this.#mediaSwap = { index, firstId };
    }
    if (interactive && slideshow && typeof slideshow.select === 'function') {
      slideshow.select(0, undefined, { animate: !reducedMotion() });
    }
  }

  /** Listes parallèles de la galerie : diapositives, miniatures, zoom. */
  #galleryParts() {
    const root = document.querySelector('.product-information__media') || document.querySelector('media-gallery');
    const slideshow = root?.querySelector('slideshow-component');
    if (!slideshow) return null;
    const slides = [...slideshow.querySelectorAll('slideshow-slide')];
    if (slides.length < 2) return null;
    const lists = [slides];
    const add = (items) => {
      if (items.length === slides.length) lists.push(items);
    };
    root.querySelectorAll('.slideshow-controls__thumbnails').forEach((c) => {
      add([...c.querySelectorAll('.slideshow-controls__thumbnail')]);
    });
    root.querySelectorAll('.dialog-zoomed-gallery').forEach((z) => add([...z.children]));
    root.querySelectorAll('.dialog-thumbnails-list').forEach((l) => {
      add([...l.children].map((li) => li.querySelector('button') || li));
    });
    return { slideshow, lists };
  }

  /** Échange le contenu de deux positions dans chaque liste, sans recréer les images. */
  #swapMedia(lists, a, b) {
    for (const items of lists) {
      const x = items[a];
      const y = items[b];
      if (!x || !y) continue;
      const fx = document.createDocumentFragment();
      const fy = document.createDocumentFragment();
      while (x.firstChild) fx.appendChild(x.firstChild);
      while (y.firstChild) fy.appendChild(y.firstChild);
      x.appendChild(fy);
      y.appendChild(fx);
    }
  }

  /** La premiere paire complete : c'est elle qui sert de modele au report. */
  #sourcePair() {
    return this.#pairs().find((p) => p.size && p.color) || null;
  }

  #hasEmptyPairs() {
    return this.#pairs().some((p) => !p.size || !p.color);
  }

  /**
   * Reporte pointure et couleur sur les paires encore vierges.
   *
   * CE QUI N'EST JAMAIS ECRASE
   * Une paire deja renseignee garde ses choix — meme partiellement remplie,
   * elle n'est pas touchee tant qu'elle porte un choix du visiteur. Chaque
   * ligne reste modifiable ensuite : le report remplit, il ne verrouille pas.
   *
   * RIEN N'EST AJOUTE AU PANIER
   * Le report prepare la selection, il ne l'achete pas. L'ajout reste le
   * geste du bouton principal.
   *
   * LE STOCK REELLEMENT SUIVI EST RESPECTE
   * Si le cumul depasse ce qui est disponible pour cette combinaison, les
   * lignes sont remplies une a une tant que Shopify peut suivre, et la
   * premiere ligne restee vide porte le message de quantite.
   */
  #applyToRemaining() {
    const source = this.#sourcePair();
    if (!source || !this.#hasEmptyPairs()) return;

    const variant = this.variants.get(key(source.color, source.size));
    if (!variant || !variant.available) return;

    const pairs = this.#pairs();
    const empties = pairs
      .map((p, i) => (!p.size && !p.color ? i : -1))
      .filter((i) => i >= 0);
    if (!empties.length) return;

    let draft = pairs.map((p) => ({ ...p }));
    for (const i of empties) draft[i] = { ...source };

    let limited = false;
    if (this.#cumulativeProblem(draft) !== false) {
      limited = true;
      draft = pairs.map((p) => ({ ...p }));
      for (const i of empties) {
        const attempt = draft.map((p, n) => (n === i ? { ...source } : p));
        if (this.#cumulativeProblem(attempt) !== false) break;
        draft = attempt;
      }
    }

    this.state.pairsByTier[this.state.tierId] = draft;
    if (this.data.packAccordion) {
      const missing = this.#firstMissing();
      this.state.openRow = missing ? missing.index : -1;
    }
    this.#clearAddedStatus();
    this.#track('c1b_apply_remaining', {
      offer_tier_id: this.state.tierId,
      quantity: this.tier.quantity,
      partial: limited,
    });
    this.#render();

    if (limited) {
      const missing = this.#firstMissing();
      if (missing) this.#setRowError(missing.index, this.data.text.errUnavailableQty);
    }
  }

  /**
   * Additionne les quantités demandées pour une même combinaison et compare
   * au stock réellement suivi. Une variante sans suivi de stock n'oppose
   * aucune limite : Shopify reste l'autorité au moment de l'ajout.
   * @returns {number|false} l'index de la paire en cause, ou false
   */
  #cumulativeProblem(pairs) {
    const counts = new Map();
    pairs.forEach((p) => {
      if (!p.size || !p.color) return;
      const k = key(p.color, p.size);
      counts.set(k, (counts.get(k) || 0) + 1);
    });
    for (let i = 0; i < pairs.length; i++) {
      const p = pairs[i];
      if (!p.size || !p.color) continue;
      const v = this.variants.get(key(p.color, p.size));
      if (!v) return i;
      if (v.limit != null && counts.get(key(p.color, p.size)) > v.limit) return i;
    }
    return false;
  }

  /** Lignes de panier : les combinaisons répétées sont fusionnées. */
  #lines() {
    const byVariant = new Map();
    for (const p of this.#pairs()) {
      const v = this.variants.get(key(p.color, p.size));
      if (!v) continue;
      byVariant.set(v.id, (byVariant.get(v.id) || 0) + 1);
    }
    return [...byVariant].map(([id, quantity]) => ({
      id,
      quantity,
      properties: { [this.data.lineProperty]: this.tier.label },
    }));
  }

  // ------------------------------------------- conduire au choix manquant

  /**
   * Hauteur à réserver en haut de la fenêtre : l'en-tête collant s'il en est
   * un, plus la garde demandée. Un en-tête plus haut repousse d'autant la
   * cible, pour qu'elle ne finisse jamais dessous.
   */
  #topInset() {
    const header = document.querySelector('#header-component, .header-section, header');
    if (!header) return CLEARANCE;
    let sticky = false;
    try {
      const position = window.getComputedStyle(header).position;
      sticky = position === 'sticky' || position === 'fixed';
    } catch {
      sticky = false;
    }
    const height = sticky ? header.getBoundingClientRect().height : 0;
    return Math.max(CLEARANCE, Math.round(height) + 24);
  }

  /**
   * Défile jusqu'à l'élément, puis exécute la suite une fois le défilement
   * réellement terminé. `scrollend` n'étant pas disponible partout, la fin
   * est constatée sur la stabilité de la position, avec une limite de temps
   * pour ne jamais rester en attente.
   */
  #scrollToElement(el, after) {
    const done = () => {
      try {
        after?.();
      } catch {
        /* la suite ne doit jamais empêcher le défilement d'avoir eu lieu */
      }
    };
    const top = Math.max(0, window.scrollY + el.getBoundingClientRect().top - this.#topInset());

    if (reducedMotion()) {
      window.scrollTo(0, top);
      requestAnimationFrame(done);
      return;
    }

    window.scrollTo({ top, behavior: 'smooth' });

    /* `behavior: 'smooth'` est ignore en silence dans certains contextes. Si
       rien n'a bouge apres quelques images, le saut direct est fait : le
       bouton fixe doit ramener au choix manquant, pas seulement le
       promettre. Un defilement reellement en cours a deja bouge ici. */
    const departure = window.scrollY;
    window.setTimeout(() => {
      if (Math.abs(window.scrollY - departure) < 2 && Math.abs(departure - top) > 2) {
        /* La feuille du theme impose `scroll-behavior: smooth` a la racine :
           un second appel serait adouci a son tour. Il est neutralise le
           temps du saut, puis rendu tel qu'il etait. */
        const root = document.documentElement;
        const previous = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';
        window.scrollTo(0, top);
        root.style.scrollBehavior = previous;
      }
    }, 320);

    const started = Date.now();
    let last = null;
    let stable = 0;
    const settle = () => {
      const now = window.scrollY;
      if (now === last) stable += 1;
      else {
        stable = 0;
        last = now;
      }
      if (stable >= 3 || Date.now() - started > 1200) return done();
      requestAnimationFrame(settle);
    };
    requestAnimationFrame(settle);
  }

  /**
   * Ouvre le panneau de la première paire incomplète, amène son premier
   * groupe de choix manquant à l'écran, puis pose le focus. Le focus est
   * posé avec `preventScroll` : le déplacement visible a déjà eu lieu, et un
   * second défilement l'annulerait.
   */
  /**
   * Amene a l'ecran le champ reellement manquant, y pose le message qui le
   * nomme, puis lui donne le focus.
   *
   * Le message est ecrit sur la LIGNE concernee, pas en pied de bloc : dans
   * un lot de quatre, « Choisissez la pointure » ne dit rien s'il flotte au
   * milieu de huit menus.
   */
  #revealFirstMissing() {
    const missing = this.#firstMissing();
    if (!missing) return;

    const { index, kind } = missing;
    if (this.data.packAccordion && this.state.openRow !== index) {
      this.state.openRow = index;
      this.#render();
    }
    const text = kind === 'size' ? this.data.text.errSize : this.data.text.errColor;
    this.#setRowError(index, text);
    this.#track('c1b_choice_error', { offer_tier_id: this.state.tierId, missing: kind, pair_index: index + 1 });

    const compose = this.#activeCompose();
    const control =
      compose?.querySelector(`[data-c1b-option="${kind}"][data-c1b-index="${index}"]:not([disabled])`) ||
      compose?.querySelector(`[data-c1b-option="${kind}"]:not([disabled])`);
    const target =
      control?.closest('.c1b-field') || control?.closest('.c1b-pair__group') || compose;
    if (!target) return;

    this.#scrollToElement(target, () => {
      if (!control) return;
      try {
        control.focus({ preventScroll: true });
      } catch {
        control.focus();
      }
    });
  }

  // ------------------------------------------------------------------ achat

  /** Bouton principal et bouton fixe portent toujours le même état. */
  #setBusy(busy) {
    this.#busy = busy;
    for (const button of this.querySelectorAll('[data-c1b-cta], .c1b-sticky__btn')) {
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }
  }

  async #submit() {
    if (this.#busy) return; // garde anti double-clic
    const tier = this.tier;

    if (this.data.packAccordion && !this.state.open && this.data.text.ctaChooseTier) {
      const list = this.querySelector('.c1b-offer__tiers');
      if (list) {
        this.#scrollToElement(list, () => {
          const radio = list.querySelector('input[name="c1b-tier"]:not([disabled])');
          try {
            radio?.focus({ preventScroll: true });
          } catch {
            radio?.focus();
          }
        });
      }
      return; // le visiteur choisit lui-même sa formule
    }

    if (!this.#complete()) {
      this.state.open = true;
      this.#render();
      this.#revealFirstMissing();
      return; // mène au champ manquant, nommé sur sa ligne, sans soumission
    }
    if (tier.blocked) {
      this.#status(this.data.text.tierBlocked, 'error');
      return;
    }

    this.#setBusy(true);
    this.#announced = false;
    this.#status(this.data.text.adding, 'neutral');

    const lines = this.#lines();
    this.#pendingLines = lines;
    const totalQty = lines.reduce((n, l) => n + l.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise?.();

    try {
      const before = await this.#readCart();

      let res;
      try {
        res = await this.#post(url('cart/add.js'), { items: lines, sections: this.#sectionIds() });
      } catch (networkError) {
        // Résultat réseau incertain : on relit avant toute nouvelle tentative.
        const after = await this.#readCart().catch(() => null);
        if (after && this.#added(before, after, lines)) res = { ok: true, body: after };
        else throw networkError;
      }

      if (!res.ok) throw new Error(res.body?.description || res.body?.message || 'add failed');

      // Code natif du palier. Un code indépendant déjà saisi n'est jamais
      // remplacé en silence : on expose le choix. Aucun événement de succès
      // n'est émis tant que ce choix n'est pas tranché.
      const own = this.#ownCodes();
      const existing = (before.discount_codes || []).filter((d) => d.code && !own.includes(d.code));
      if (tier.code && existing.length) {
        this.#pendingCode = tier.code;
        this.#pendingExisting = existing.map((d) => d.code);
        const cart = await this.#readCart();
        this.#status('', 'neutral');
        this.#renderConflict(cart);
        deferred?.resolve?.({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse?.(cart),
          detail: { didError: false, items: cart.items, source: 'arpivia-c1b-offer', itemCount: totalQty },
        });
        return;
      }

      if (tier.code) await this.#applyCode(tier.code);

      // Le palier réellement dû dépend du panier entier, pas de la seule
      // formule cliquée : l'arbitrage a lieu avant toute annonce, pour que la
      // valeur mesurée soit celle que le client paiera.
      const cart = await this.#settleDiscount();

      // Toutes les paires doivent être là : un lot incomplet n'est jamais
      // annoncé comme ajouté.
      if (!this.#added(before, cart, lines)) {
        throw new Error('lot incomplet dans le panier');
      }

      // Le tarif de la formule n'est annoncé que si Shopify a retenu l'un de
      // ses codes. Sinon les paires sont bien là, mais au prix réel du panier.
      const discountOk = !tier.code || this.#ownDiscountApplied(cart);

      await this.#announce(cart, lines, totalQty, deferred, discountOk ? null : this.data.text.discountMissing);
      this.#finish(cart, lines, totalQty, tier.id, discountOk ? {} : { discount_applied: false });
    } catch (err) {
      // Les choix sont conservés : rien n'est réinitialisé.
      this.#status(this.data.text.addFailed, 'error');
      deferred?.reject?.(err);
      if (err?.name !== 'AbortError') console.warn('[c1b-offer]', err);
    } finally {
      this.#setBusy(false);
      this.#render();
    }
  }

  /** Un code de cette offre est-il réellement appliqué au panier ? */
  #ownDiscountApplied(cart) {
    const own = this.#ownCodes();
    return (cart.discount_codes || []).some((d) => own.includes(d.code) && d.applicable !== false);
  }

  /**
   * Codes de cette offre : ceux du bloc, plus tous les paliers connus du
   * recalcul du panier (3 paires, 5 paires…). Un code de palier intermédiaire
   * déjà présent n'est donc jamais pris pour le code d'un autre client.
   */
  #ownCodes() {
    const fromBlock = (this.data.ownCodes || []).filter(Boolean);
    const fromCart = (window.ArpiviaC1bCartSync && window.ArpiviaC1bCartSync.config && window.ArpiviaC1bCartSync.config.codes) || [];
    return [...new Set([...fromBlock, ...fromCart])];
  }

  async #resolveConflict(choice) {
    const lines = this.#pendingLines.length ? this.#pendingLines : this.#lines();
    const totalQty = lines.reduce((n, l) => n + l.quantity, 0);
    try {
      if (choice === 'offer') await this.#applyCode(this.#pendingCode);
      // « Conserver mon code » ne supprime rien : le panier est relu tel quel.
      const cart = choice === 'offer' ? await this.#settleDiscount() : await this.#readCart();
      this.#conflictBox()?.remove();
      await this.#announce(cart, lines, totalQty, null);
      this.#finish(cart, lines, totalQty, this.tier.id, { conflict: choice });
    } catch (err) {
      this.#status(this.data.text.addFailed, 'error');
      console.warn('[c1b-offer] conflit', err);
    }
  }

  /**
   * Laisse le module de panier choisir le meilleur palier réellement atteint,
   * puis renvoie l'état retourné par Shopify. Sans ce module, le panier est
   * simplement relu : aucun prix n'est deviné.
   */
  async #settleDiscount() {
    const sync = window.ArpiviaC1bCartSync;
    if (sync && typeof sync.sync === 'function') {
      const cart = await sync.sync({ render: false, reason: 'offer-add' }).catch(() => null);
      if (cart && cart.item_count != null) return cart;
    }
    return this.#readCart();
  }

  async #applyCode(code) {
    const r = await this.#post(url('cart/update.js'), { discount: code, sections: this.#sectionIds() });
    return r.body?.item_count != null ? r.body : await this.#readCart();
  }

  /**
   * Publie l'ajout, rafraîchit les sections du panier, ouvre le tiroir.
   *
   * L'ÉVÉNEMENT DU THÈME EXIGE UNE PROMESSE
   * Ses écouteurs appellent `event.promise.then(...)` sans garde, et sa
   * construction échoue si le champ est vide. L'appelant n'en fournit pas
   * toujours une — c'est le cas après l'arbitrage d'un conflit de codes, où
   * l'ajout a eu lieu bien avant le choix. Une promesse est donc créée ici à
   * défaut, faute de quoi un ajout réussi serait annoncé comme un échec.
   */
  async #announce(cart, lines, totalQty, deferred, warning = null) {
    const payload = {
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse?.(cart),
      detail: { didError: false, items: cart.items, source: 'arpivia-c1b-offer', itemCount: totalQty },
    };
    const settle =
      deferred ||
      (typeof CartLinesUpdateEvent.createPromise === 'function' ? CartLinesUpdateEvent.createPromise() : null);

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
        promise: settle ? settle.promise : Promise.resolve(payload),
      })
    );
    settle?.resolve?.(payload);

    await this.#refreshSections();
    await this.#openDrawer();
    if (warning) this.#status(warning, 'error');
    else this.#status(this.data.text.added, 'success');
  }

  /**
   * Événement de succès, une seule fois par ajout.
   *
   * `value` décrit la valeur des unités RÉELLEMENT AJOUTÉES, après
   * répartition de la remise applicable sur ces unités — jamais le total du
   * panier, qui est exposé à part sous `cart_total`. Une remise qui
   * réévalue aussi les anciennes lignes ne gonfle donc pas cette valeur :
   * seules les quantités ajoutées sont multipliées par le prix unitaire
   * réellement retourné par Shopify.
   *
   * Aucun événement publicitaire standard n'est émis ici : l'ajout au panier
   * est déjà publié par les intégrations natives de la boutique.
   */
  #finish(cart, lines, totalQty, tierId, extra = {}) {
    if (this.#announced) return;
    this.#announced = true;

    const unit = (variantId) => {
      const item = (cart.items || []).find((i) => Number(i.variant_id) === Number(variantId));
      if (!item) return null;
      if (item.quantity > 0 && typeof item.final_line_price === 'number') {
        return item.final_line_price / item.quantity;
      }
      return typeof item.final_price === 'number' ? item.final_price : null;
    };

    let added = 0;
    let measurable = true;
    for (const line of lines) {
      const price = unit(line.id);
      if (price == null) {
        measurable = false;
        break;
      }
      added += price * line.quantity;
    }

    this.#track('c1b_add_success', {
      offer_tier_id: tierId,
      quantity: totalQty,
      // Une valeur qu'on ne sait pas calculer reste absente : rien n'est inventé.
      ...(measurable ? { value: added / 100 } : {}),
      cart_total: typeof cart.total_price === 'number' ? cart.total_price / 100 : undefined,
      currency: cart.currency,
      items: lines.map((l) => ({ variant_id: Number(l.id), quantity: l.quantity })),
      ...extra,
    });
  }

  #sectionIds() {
    return [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((n) => n.dataset.sectionId)
      .filter(Boolean);
  }

  /**
   * Rafraîchit le contenu du panier.
   *
   * L'API de rendu de sections n'est utilisable que si un identifiant de
   * section est réellement présent dans le document. Sur un panier vide,
   * `cart-items-component` n'est pas encore rendu : sans repli, le tiroir
   * s'ouvrirait sur son contenu vide juste après un ajout réussi. Le repli
   * relit la page courante et remplace le tiroir par sa version à jour.
   */
  async #refreshSections() {
    const ids = this.#sectionIds();
    let drawerRefreshed = false;

    if (ids.length) {
      try {
        const r = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`, {
          credentials: 'same-origin',
        });
        const data = await r.json();
        for (const id of ids) {
          const html = data[id];
          if (!html) continue;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh =
            doc.querySelector(`#shopify-section-${id}, [data-section-id="${id}"]`) ||
            doc.body.firstElementChild;
          const live = document.querySelector(`#shopify-section-${id}`);
          if (fresh && live) {
            live.replaceChildren(...fresh.childNodes);
            if (id.includes('cart-drawer')) drawerRefreshed = true;
          }
        }
      } catch (e) {
        console.warn('[c1b-offer] sections', e);
      }
    }

    if (drawerRefreshed) return;

    try {
      const r = await fetch(window.location.pathname, { credentials: 'same-origin', cache: 'no-store' });
      const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
      const fresh = doc.querySelector('#shopify-section-cart-drawer-section');
      const live = document.querySelector('#shopify-section-cart-drawer-section');
      if (fresh && live) live.replaceChildren(...fresh.childNodes);
    } catch (e) {
      console.warn('[c1b-offer] drawer refresh', e);
    }
  }

  async #openDrawer() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (!drawer) return;
    await customElements.whenDefined('theme-drawer').catch(() => {});
    // `open()` gère la mise au focus, la fermeture par Échap et le retour du
    // focus : c'est le comportement natif du thème, il n'est pas réécrit ici.
    if (typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
  }

  // ------------------------------------------------------------------ réseau

  async #post(u, body) {
    const r = await fetch(u, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    let parsed = null;
    try {
      parsed = await r.json();
    } catch {
      /* corps illisible */
    }
    return { ok: r.ok, status: r.status, body: parsed };
  }

  async #readCart() {
    const r = await fetch(url('cart.js'), { credentials: 'same-origin', cache: 'no-store' });
    return r.json();
  }

  /** Un échec HTTP ne garantit pas l'absence de modification : on compare. */
  #added(before, after, lines) {
    if (!before || !after) return false;
    const q = (cart, id) =>
      (cart.items || []).filter((i) => Number(i.variant_id) === Number(id)).reduce((n, i) => n + i.quantity, 0);
    return lines.every((l) => q(after, l.id) >= q(before, l.id) + l.quantity);
  }

  // ------------------------------------------------------------------ mesure

  #track(name, payload) {
    // Aucun événement de conversion n'est émis ici : l'ajout au panier est
    // déjà publié par l'intégration native de Shopify. Ces repères servent
    // uniquement à suivre les étapes du parcours.
    const detail = { ...payload, ...this.data.context, ...campaignContext() };
    for (const k of Object.keys(detail)) if (detail[k] === undefined) delete detail[k];
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...detail });
    document.dispatchEvent(new CustomEvent(`arpivia:${name}`, { detail, bubbles: true }));
  }

  // ------------------------------------------------------------------ rendu

  #cta() {
    return this.querySelector('[data-c1b-cta]');
  }

  #conflictBox() {
    return this.querySelector('[data-c1b-conflict]');
  }

  #status(msg, tone) {
    const el = this.querySelector('[data-c1b-status]');
    if (!el) return;
    el.textContent = msg;
    el.dataset.tone = tone;
  }

  /** La zone de composition affichee, celle de la formule cochee. */
  #activeCompose() {
    return this.querySelector(`[data-c1b-compose-tier="${this.state.tierId}"]`);
  }

  /** Message porte par une ligne precise, jamais par le bloc entier. */
  #setRowError(index, msg) {
    const compose = this.#activeCompose();
    if (!compose) return;
    const el = compose.querySelector(`[data-c1b-row-error="${index}"]`);
    if (el) el.textContent = msg;
  }

  #clearRowErrors() {
    for (const el of this.querySelectorAll('[data-c1b-row-error]')) el.textContent = '';
  }

  /**
   * Retire l'annonce d'un ajout precedent.
   *
   * POURQUOI
   * « Votre selection a ete ajoutee au panier » decrit un ajout precis :
   * cette formule, ces pointures, ces couleurs. Des que l'un des trois
   * change, la phrase ne decrit plus ce qui est a l'ecran, et elle se lisait
   * comme une confirmation du nouveau choix — qui n'a pourtant rien ajoute.
   *
   * Seule l'annonce de succes est effacee. Un message d'erreur, lui, porte
   * sur une tentative reelle et doit rester lisible tant qu'il n'a pas ete
   * remplace par le resultat d'une nouvelle tentative.
   *
   * La zone est `aria-live="polite"` : la vider ne produit aucune annonce.
   */
  #clearAddedStatus() {
    const el = this.querySelector('[data-c1b-status]');
    if (!el || el.dataset.tone !== 'success') return;
    el.textContent = '';
    el.dataset.tone = 'neutral';
    // Le prochain ajout reussi doit pouvoir s'annoncer a son tour.
    this.#announced = false;
  }

  #renderConflict(cart) {
    const t = this.data.text;
    const box = document.createElement('div');
    box.className = 'c1b-sync-warning';
    box.setAttribute('data-c1b-conflict', '');
    box.setAttribute('role', 'alert');
    box.innerHTML =
      `<strong>${t.conflictTitle}</strong>` +
      `<span>${t.conflictBody.replace('[codes]', this.#pendingExisting.join(', '))}</span>`;
    const actions = document.createElement('p');
    actions.className = 'c1b-pair__actions';
    actions.innerHTML =
      `<button type="button" class="c1b-btn" data-c1b-action="apply-offer">${t.conflictUseOffer}</button>` +
      `<button type="button" class="c1b-btn c1b-btn--quiet" data-c1b-action="keep-code">${t.conflictKeepCode}</button>`;
    box.appendChild(actions);
    this.querySelector('[data-c1b-actions]')?.prepend(box);
    void cart;
  }

  /**
   * Un seul rendu, appele apres chaque changement d'etat.
   *
   * IL NE RECONSTRUIT RIEN
   * Toutes les cartes et toutes les lignes existent deja dans le document :
   * ce rendu ne fait que refleter l'etat sur des controles en place. C'est
   * ce qui permet au menu natif ouvert de ne pas se refermer sous le doigt.
   */
  #render() {
    const tier = this.tier;
    const t = this.data.text;
    const pairs = this.#pairs();
    const single = tier.quantity < 2;
    this.dataset.c1bSingle = String(single);

    // ---- Cartes : une seule est cochee, une seule se deplie.
    for (const el of this.querySelectorAll('[data-c1b-tier]')) {
      const id = el.dataset.c1bTier;
      const selected = id === this.state.tierId;
      const input = el.querySelector('input[name="c1b-tier"]');
      // Repliée à l'arrivée, aucune formule n'apparaît cochée : le visiteur
      // choisit lui-même. Le bouton d'achat ouvre la formule par défaut.
      // Réglage « Garder la formule par défaut cochée » : la formule reste cochée
      // même repliée ; seule sa composition attend le premier clic.
      const shown = selected && (this.state.open || Boolean(this.data.keepDefault));
      if (input) input.checked = shown;
      el.dataset.selected = String(shown);
      const compose = el.querySelector('[data-c1b-compose-tier]');
      if (compose) compose.hidden = !selected || !this.state.open;
    }
    this.dataset.c1bOpen = String(this.state.open);

    const compose = this.#activeCompose();

    // ---- Controles de la formule affichee.
    if (compose) {
      for (const control of compose.querySelectorAll('[data-c1b-option]')) {
        const kind = control.dataset.c1bOption;
        const index = Number(control.dataset.c1bIndex || 0);
        const pair = pairs[index];
        if (!pair) continue;

        if (control instanceof HTMLSelectElement) {
          const wanted = pair[kind] || '';
          if (control.value !== wanted) control.value = wanted;
          for (const option of control.options) {
            if (!option.value) continue;
            option.disabled = !this.#optionPossible(pairs, index, kind, option.value);
          }
          const field = control.closest('.c1b-field');
          if (field) field.dataset.filled = String(Boolean(pair[kind]));
          continue;
        }

        // Boutons et pastilles : un bouton radio par valeur.
        control.checked = pair[kind] === control.value;
        const ok = this.#optionPossible(pairs, index, kind, control.value);
        control.disabled = !ok;
        control.closest('.c1b-opt, .c1b-swatch')?.setAttribute('data-unavailable', String(!ok));
        const group = control.closest('.c1b-field');
        if (group) group.dataset.filled = String(Boolean(pair[kind]));
      }

      // Nom de la couleur choisie, écrit à côté du libellé des pastilles.
      for (const name of compose.querySelectorAll('[data-c1b-color-name]')) {
        const pair = pairs[Number(name.dataset.c1bColorName)];
        name.textContent = pair && pair.color ? pair.color : '';
      }

      if (this.data.packAccordion) {
        if (this.state.openRow == null) {
          const first = this.#firstMissing();
          this.state.openRow = first ? first.index : -1;
        }
        for (const panel of compose.querySelectorAll('[data-c1b-row-panel]')) {
          const i = Number(panel.dataset.c1bRowPanel);
          const open = i === this.state.openRow;
          panel.hidden = !open;
          const toggle = compose.querySelector(`[data-c1b-row-toggle="${i}"]`);
          if (toggle) toggle.setAttribute('aria-expanded', String(open));
          const summary = compose.querySelector(`[data-c1b-row-summary="${i}"]`);
          const pair = pairs[i] || {};
          if (summary) {
            const filled = pair.size || pair.color;
            summary.textContent = filled
              ? [pair.size || '…', pair.color || '…'].join(' · ')
              : t.pairPending || '';
            summary.dataset.state = pair.size && pair.color ? 'complete' : filled ? 'partial' : 'empty';
          }
        }
      }

      const applyRest = compose.querySelector('[data-c1b-action="apply-rest"]');
      if (applyRest) {
        applyRest.hidden = single || !this.#sourcePair() || !this.#hasEmptyPairs();
      }
    }

    // ---- Bouton principal, puis son miroir fixe : meme libelle, meme etat.
    const done = this.#complete();
    const missing = done ? null : this.#firstMissing();
    let label;
    const chooseTier = Boolean(this.data.packAccordion && !this.state.open && t.ctaChooseTier);
    // État lu par la feuille du mode accordéon : bouton « en attente » tant que
    // le pack n'est pas prêt, bouton plein une fois tout choisi.
    this.dataset.c1bComplete = String(Boolean(done && !chooseTier));
    if (chooseTier) {
      label = t.ctaChooseTier;
    } else if (done) {
      label = t.ctaAdd;
    } else if (single) {
      label = missing && missing.kind === 'color' && this.state.started && t.ctaChooseColor ? t.ctaChooseColor : t.ctaComposeOne;
    } else if (missing && this.state.started) {
      const template = missing.kind === 'size' ? t.ctaPairSize : t.ctaPairColor;
      label = template ? template.replace('[n]', String(missing.index + 1)) : t.ctaComposeMany;
    } else {
      label = t.ctaComposeMany;
    }
    // Un libellé d'ajout qui contient [total] porte le montant lui-même.
    const inline = !chooseTier && done && typeof label === 'string' && label.includes('[total]');
    if (inline) label = label.replace('[total]', tier.priceFormatted || '').replace(/\s*[—-]\s*$/, '');
    const showAmount = !chooseTier && done && !inline && Boolean(tier.priceFormatted);

    const cta = this.#cta();
    if (cta) {
      const labelEl = cta.querySelector('[data-c1b-cta-label]');
      const amountEl = cta.querySelector('[data-c1b-cta-amount]');
      if (labelEl) labelEl.textContent = label;
      if (amountEl) {
        amountEl.textContent = showAmount ? tier.priceFormatted : '';
        amountEl.hidden = !showAmount;
      }
      cta.disabled = Boolean(tier.blocked);
    }

    const sLabel = this.querySelector('[data-c1b-sticky-label]');
    const sAmount = this.querySelector('[data-c1b-sticky-amount]');
    const sBtn = this.querySelector('.c1b-sticky__btn');
    const shortAdd = !chooseTier && done && t.stickyAdd;
    if (sLabel) sLabel.textContent = shortAdd ? t.stickyAdd.replace('[total]', tier.priceFormatted || '').trim() : label;
    if (sAmount) {
      const amount = showAmount && !shortAdd;
      sAmount.textContent = amount ? tier.priceFormatted : '';
      sAmount.hidden = !amount;
    }
    if (sBtn) sBtn.disabled = Boolean(tier.blocked);

    this.#renderStock(pairs);

    // Récapitulatif de la barre fixe : produit, formule et choix réels.
    const sSummary = this.querySelector('[data-c1b-sticky-summary]');
    if (sSummary && t.stickySummary) {
      const first = pairs[0] || {};
      const choice = single && first.size && first.color ? `${first.size} · ${first.color}` : '';
      const text = t.stickySummary
        .replace('[produit]', this.data.productTitle || '')
        .replace('[formule]', tier.label || '')
        .replace('[prix]', tier.priceFormatted || '')
        .replace('[choix]', choice)
        .replace(/(\s*·\s*)+(?=\s*·)/g, '')
        .replace(/(\s*·\s*)+$/, '')
        .trim();
      sSummary.textContent = text;
      if (this.data.packAccordion && this.state.open) {
        const n = Number(tier.quantity) || 1;
        const count = (n > 1 ? t.stickyCountMany : t.stickyCountOne) || '';
        if (count) {
          const small = document.createElement('small');
          small.className = 'c1b-sticky__count';
          small.textContent = count.replace('[n]', String(n));
          sSummary.append(small);
        }
      }
      sSummary.hidden = !text;
    }
  }

  /**
   * QUANTITÉ SAISIE À LA MAIN
   *
   * Le réglage accepte une ligne par combinaison, `Coloris|Pointure=Reste`,
   * et une ligne `*=Reste` qui sert de valeur par défaut aux combinaisons
   * non listées. Les espaces et les majuscules sont ignorés, les lignes
   * commençant par `#` sont des commentaires. Une combinaison absente et
   * sans valeur par défaut renvoie `null` : la ligne dit alors seulement la
   * disponibilité, sans chiffre — jamais un chiffre inventé.
   * @param {{color: string, size: string}} pair
   * @returns {number|null}
   */
  #stockSaisi(pair) {
    const brut = String(this.data.stock?.manuel || '');
    const norm = (v) => String(v || '').trim().toLowerCase();
    if (!this.#stockTable) {
      this.#stockTable = new Map();
      for (const ligne of brut.split(/[\r\n]+/)) {
        const texte = ligne.trim();
        if (!texte || texte.startsWith('#')) continue;
        const coupe = texte.indexOf('=');
        if (coupe < 0) continue;
        const reste = Number(texte.slice(coupe + 1).trim());
        if (!Number.isFinite(reste)) continue;
        const [couleur, taille] = texte.slice(0, coupe).split('|');
        this.#stockTable.set(norm(couleur) + '|' + norm(taille), reste);
      }
    }
    const exact = this.#stockTable.get(norm(pair.color) + '|' + norm(pair.size));
    if (exact !== undefined) return exact;
    const defaut = this.#stockTable.get('*|');
    return defaut === undefined ? null : defaut;
  }

  /**
   * Ligne de stock pour la dernière paire complétée.
   * Démonstration : quantité saisie dans l'éditeur (la mention est dans le
   * HTML). Stock réel : quantité d'inventaire Shopify si la variante est
   * suivie ; sinon seule la disponibilité est dite.
   */
  #renderStock(pairs) {
    const line = this.querySelector('[data-c1b-stock]');
    const cfg = this.data.stock;
    if (!line || !cfg || cfg.mode === 'off') return;
    const text = line.querySelector('[data-c1b-stock-text]');
    const last = pairs[this.state.lastIndex ?? -1];
    const pair = last && last.size && last.color ? last : pairs.find((p) => p.size && p.color);
    const variant = pair ? this.variants.get(key(pair.color, pair.size)) : null;
    if (!variant) {
      line.hidden = true;
      return;
    }
    let message = '';
    let state = 'available';
    if (!variant.available) {
      message = cfg.unavailable;
      state = 'unavailable';
    } else {
      let qty = variant.stock;
      if (cfg.mode === 'demo') qty = Number(cfg.demoQuantity);
      if (cfg.mode === 'manuel') qty = this.#stockSaisi(pair);
      if (cfg.mode === 'manuel' && qty === 0) {
        if (text) text.textContent = cfg.unavailable;
        line.dataset.state = 'unavailable';
        line.hidden = !cfg.unavailable;
        return;
      }
      if (qty != null && qty > 0 && qty <= Number(cfg.threshold)) {
        message = String(cfg.low || '').replace('[n]', String(qty));
        state = 'low';
      } else if (cfg.mode === 'demo' || cfg.mode === 'manuel' || variant.stock != null) {
        message = cfg.available;
      } else {
        message = cfg.available;
      }
    }
    if (text) text.textContent = message;
    line.dataset.state = state;
    line.hidden = !message;
  }

  /**
   * Cette valeur peut-elle etre choisie ici ?
   *
   * Deux conditions, dans cet ordre : la combinaison qui en resulterait doit
   * exister et etre disponible, et le cumul de la formule entiere doit rester
   * dans le stock reellement suivi. Une variante sans suivi de stock n'oppose
   * aucune limite — Shopify reste l'autorite au moment de l'ajout.
   */
  #optionPossible(pairs, index, kind, value) {
    const draft = pairs.map((p, n) => (n === index ? { ...p, [kind]: value } : p));
    const pair = draft[index];

    if (pair.size && pair.color) {
      const variant = this.variants.get(key(pair.color, pair.size));
      if (!variant || !variant.available) return false;
    } else {
      // Un seul champ renseigne : la valeur doit rester atteignable avec au
      // moins une valeur de l'autre champ.
      const partial = [...this.variants.values()].some((v) => {
        if (!v.available) return false;
        return kind === 'size' ? v.size === value : v.color === value;
      });
      if (!partial) return false;
    }

    return this.#cumulativeProblem(draft) === false;
  }
}

if (!customElements.get('arpivia-c1b-offer')) {
  customElements.define('arpivia-c1b-offer', ArpiviaC1bOffer);
}

export { ArpiviaC1bOffer, StandardEvents };
